/**
 * AOS Module System Types
 *
 * Defines the structure for loadable AOS modules (apps) including:
 * - Module manifests with metadata and permissions
 * - Permission system for sandboxed execution
 * - Boot image format for secure boot
 */

import type { MultiSigEnvelope, MultiSigPolicy } from '../identity/multisig.js';

// ============================================================================
// Module Permissions
// ============================================================================

/**
 * Permissions that modules can request.
 * Categorized by risk level for UI prompts.
 */
export type ModulePermission =
  // Safe - auto-granted
  | 'dag:read'              // Read any DAG object
  | 'identity:read'         // Read own public key
  | 'storage:local'         // Access module-local storage
  | 'ui:notification'       // Show notifications

  // Prompt - requires user approval
  | 'dag:write'             // Write DAG objects (own namespace)
  | 'identity:sign'         // Sign with user's key
  | 'clipboard:read'        // Read clipboard history
  | 'clipboard:write'       // Write to clipboard
  | 'payment:request'       // Request Brain Pay payment
  | 'ui:fullscreen'         // Request fullscreen mode

  // Dangerous - warning + approval
  | 'dag:write:global'      // Write to any namespace
  | 'network:peers'         // Access peer list
  | 'network:connect';      // Establish P2P connections

/**
 * Permission categories for UI display
 */
export type PermissionCategory = 'safe' | 'prompt' | 'dangerous';

/**
 * Map of permissions to their categories
 */
export const PERMISSION_CATEGORIES: Record<ModulePermission, PermissionCategory> = {
  // Safe
  'dag:read': 'safe',
  'identity:read': 'safe',
  'storage:local': 'safe',
  'ui:notification': 'safe',

  // Prompt
  'dag:write': 'prompt',
  'identity:sign': 'prompt',
  'clipboard:read': 'prompt',
  'clipboard:write': 'prompt',
  'payment:request': 'prompt',
  'ui:fullscreen': 'prompt',

  // Dangerous
  'dag:write:global': 'dangerous',
  'network:peers': 'dangerous',
  'network:connect': 'dangerous',
};

/**
 * Human-readable descriptions of permissions
 */
export const PERMISSION_DESCRIPTIONS: Record<ModulePermission, string> = {
  'dag:read': 'Read content from the distributed storage',
  'identity:read': 'Access your public key',
  'storage:local': 'Store data locally for this module',
  'ui:notification': 'Show notifications',

  'dag:write': 'Create and store content',
  'identity:sign': 'Sign messages with your identity',
  'clipboard:read': 'Read your clipboard history',
  'clipboard:write': 'Add items to your clipboard',
  'payment:request': 'Request payments via Brain Pay',
  'ui:fullscreen': 'Enter fullscreen mode',

  'dag:write:global': 'Write content to any namespace (dangerous)',
  'network:peers': 'See who you are connected to',
  'network:connect': 'Connect to other peers directly',
};

/**
 * Get all permissions in a category
 */
export function getPermissionsByCategory(category: PermissionCategory): ModulePermission[] {
  return (Object.entries(PERMISSION_CATEGORIES) as [ModulePermission, PermissionCategory][])
    .filter(([, cat]) => cat === category)
    .map(([perm]) => perm);
}

// ============================================================================
// Module Manifest
// ============================================================================

/**
 * Author information for a module
 */
export interface ModuleAuthor {
  /** Author's public key (base58) */
  pubkey: string;
  /** Optional human-readable alias */
  alias?: string;
  /** Optional organization */
  org?: string;
}

/**
 * Module manifest - the core metadata and signature structure for AOS modules
 */
export interface ModuleManifest {
  /** Content type identifier */
  type: 'aos.module';

  /** Unique module identifier (e.g., 'commune', 'brain-pay', 'wiki') */
  id: string;

  /** Semantic version (e.g., '1.0.0') */
  version: string;

  /** Human-readable name */
  name: string;

  /** Module description */
  description: string;

  /** Author information */
  author: ModuleAuthor;

  /** Creation timestamp */
  created: number;

  /** DAG hash of main JavaScript bundle */
  entry: string;

  /** Optional additional code chunks for lazy loading */
  chunks?: string[];

  /** Total size in bytes (entry + chunks) */
  size: number;

  /** SHA-256 hash of concatenated code (entry + chunks in order) */
  contentHash: string;

  /** Permissions requested by this module */
  permissions: ModulePermission[];

  /** Multi-signature envelope for verification */
  signatures: MultiSigEnvelope;

  /** Chain information for updates */
  chain: {
    /** DAG hash of previous version (null for first version) */
    previous: string | null;
    /** Monotonically increasing sequence number */
    sequence: number;
    /** Human-readable changelog */
    changelog: string;
  };

  /** Optional metadata */
  meta?: {
    /** Module homepage URL */
    homepage?: string;
    /** Repository URL */
    repository?: string;
    /** License identifier */
    license?: string;
    /** Keywords for discovery */
    keywords?: string[];
    /** Minimum AOS version required */
    minAosVersion?: string;
    /** Module icon (DAG hash of image) */
    icon?: string;
    /** Screenshots (DAG hashes) */
    screenshots?: string[];
  };
}

/**
 * Unsigned module manifest (before signatures are added)
 */
export type UnsignedModuleManifest = Omit<ModuleManifest, 'signatures'>;

// ============================================================================
// Module Code
// ============================================================================

/**
 * Module code chunk stored in DAG
 */
export interface ModuleCode {
  /** Content type identifier */
  type: 'aos.module.code';

  /** Module ID this code belongs to */
  moduleId: string;

  /** Module version */
  moduleVersion: string;

  /** Chunk index (0 for entry, 1+ for additional chunks) */
  chunkIndex: number;

  /** JavaScript source code */
  code: string;

  /** SHA-256 hash of code */
  hash: string;

  /** Size in bytes */
  size: number;
}

// ============================================================================
// Boot Image
// ============================================================================

/**
 * Boot image manifest for secure boot
 */
export interface BootImage {
  /** Content type identifier */
  type: 'aos.boot';

  /** Semantic version */
  version: string;

  /** Creation timestamp */
  created: number;

  /** Author information */
  author: ModuleAuthor;

  /** Boot content */
  content: {
    /** SHA-256 hash of boot bundle */
    hash: string;
    /** Total size in bytes */
    size: number;
    /** DAG hashes of code chunks */
    chunks: string[];
  };

  /** Chain information */
  chain: {
    /** DAG hash of previous boot image */
    previous: string | null;
    /** Monotonically increasing sequence number */
    sequence: number;
    /** Human-readable changelog */
    changelog: string;
  };

  /** Multi-signature envelope */
  signatures: MultiSigEnvelope;

  /** Trust policy for this boot image */
  trust: MultiSigPolicy;
}

/**
 * Unsigned boot image (before signatures)
 */
export type UnsignedBootImage = Omit<BootImage, 'signatures'>;

// ============================================================================
// Key Revocation
// ============================================================================

/**
 * Key revocation notice
 */
export interface KeyRevocation {
  /** Content type identifier */
  type: 'aos.key.revocation';

  /** Public key being revoked (base58) */
  revokedKey: string;

  /** Public key of authority revoking (base58) */
  revokedBy: string;

  /** Timestamp of revocation */
  timestamp: number;

  /** Reason for revocation */
  reason: string;

  /** Signature by revokedBy key */
  signature: string;
}

// ============================================================================
// Permission Grant
// ============================================================================

/**
 * Permission grant for module
 */
export interface PermissionGrant {
  /** Content type identifier */
  type: 'aos.permission.grant';

  /** Module ID */
  moduleId: string;

  /** Module version (or '*' for any) */
  moduleVersion: string;

  /** User granting permission (base58 pubkey) */
  grantedBy: string;

  /** Permissions being granted */
  permissions: ModulePermission[];

  /** Timestamp of grant */
  timestamp: number;

  /** Optional expiration */
  expires?: number;

  /** Signature by grantedBy */
  signature: string;
}

// ============================================================================
// Loaded Module
// ============================================================================

/**
 * A fully loaded and verified module ready for execution
 */
export interface LoadedModule {
  /** The verified manifest */
  manifest: ModuleManifest;

  /** Concatenated JavaScript code */
  code: string;

  /** Whether signatures were verified */
  verified: boolean;

  /** Public keys of trusted signers who signed this */
  trustedSigners: string[];

  /** Permissions granted by user (may be subset of requested) */
  grantedPermissions: ModulePermission[];

  /** Timestamp when loaded */
  loadedAt: number;
}

// ============================================================================
// Validation Result
// ============================================================================

/**
 * Result of manifest validation
 */
export interface ManifestValidationResult {
  /** Whether the manifest is valid */
  valid: boolean;

  /** Validation errors (if any) */
  errors: string[];

  /** Validation warnings (non-fatal) */
  warnings: string[];
}

/**
 * Result of signature verification for a module
 */
export interface ModuleVerifyResult {
  /** Whether enough valid signatures from trusted signers */
  valid: boolean;

  /** Number of valid signatures */
  validCount: number;

  /** Required threshold */
  threshold: number;

  /** Trusted signers who signed */
  trustedSigners: string[];

  /** All signature details */
  signatureDetails: {
    signer: string;
    valid: boolean;
    trusted: boolean;
    error?: string;
  }[];

  /** Errors encountered */
  errors: string[];
}

/**
 * Result of permission check
 */
export interface PermissionCheckResult {
  /** Permissions that can be auto-granted (safe) */
  safe: ModulePermission[];

  /** Permissions requiring user prompt */
  prompt: ModulePermission[];

  /** Permissions that are dangerous (extra warning) */
  dangerous: ModulePermission[];

  /** Any unrecognized permissions */
  unknown: string[];
}
